import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  Lock,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Phase 3 piece targeting "local AI file organizer mac" and "private AI file
 * organizer". Privacy wedge against Sparkle (cloud-only) from the demand
 * mining doc. Honest about the tradeoffs: local is slower and slightly less
 * accurate, cloud is faster and more accurate, and there are situations where
 * each is the right pick. Walkthrough of how to switch in Sortio so the post
 * is actionable, not just opinion.
 */

const PUBLISHED = '2026-05-14';
const CANONICAL = 'https://www.getsortio.com/blog/local-ai-vs-cloud-ai-file-organization';

const FAQ = [
  {
    question: 'Does my file content leave my Mac when I use Sortio?',
    answer:
      'Only if you choose the managed AI option (Sortio-hosted or BYOK with your own provider). In Ollama local mode, your file content is read by an LLM running on your Mac and never leaves the machine. The only network traffic is a Sortio license check on launch. For sensitive files (medical, legal, financial, NDA-covered work) local mode is the right pick.',
  },
  {
    question: 'How fast is local AI compared to cloud?',
    answer:
      'Slower, but not impractically so. A typical 100-file sort on Llama 3 8B on an M2 or newer Mac takes 90 seconds to three minutes; the same sort on the managed cloud option takes 20 to 40 seconds. The gap widens for very large sorts (1,000+ files) because cloud parallelizes harder than a single Mac can. Local mode is the right pick when privacy outweighs the wait.',
  },
  {
    question: 'How does accuracy compare?',
    answer:
      'Cloud is more accurate. The frontier models the managed option uses are larger than anything that fits in 16GB of RAM, and they make fewer classification mistakes. For straightforward file types (invoices, screenshots, photos, common document types) the gap is small enough that most users do not notice. For ambiguous content (a vendor that could be classified two ways, a document whose category depends on context), cloud is clearly better. Local is honest: it sometimes asks for clarification or makes a less confident decision.',
  },
  {
    question: 'Can I run Sparkle or other cloud-only AI organizers locally?',
    answer:
      'No. Sparkle requires an internet connection because its AI runs in the cloud. It is one of the recurring complaints in the AI-file-organizer category: "only if it works completely local and offline" is the single most repeated must-have on HN threads. Sortio supports Ollama local mode for exactly this reason.',
  },
  {
    question: 'What model should I run locally?',
    answer:
      'Llama 3 8B is the sensible default for most Macs. It fits in 8-12GB of RAM, runs at acceptable speed on M-series chips, and handles the structured-extraction prompts Sortio uses for file routing. Mistral 7B is the alternative for users who prefer it. For users with 32GB+ RAM and an M3 Pro or M4, Llama 3 70B-Q4 quantized is the next step up; it is meaningfully better at ambiguous classifications but takes more setup.',
  },
  {
    question: 'Does BYOK count as local?',
    answer:
      "No. BYOK (bring your own key) means Sortio routes inference through your own API key with a provider like OpenAI or Anthropic. The provider sees your file content, just billed to you instead of to Sortio. This is the right pick when you trust a specific provider (or your enterprise has a contract with one) but want Sortio's UX. Local mode is different: the model runs on your machine, no provider is involved.",
  },
  {
    question: 'Can I switch between modes mid-project?',
    answer:
      'Yes. The mode is set per workspace in Sortio settings, and you can toggle it at any time. A common pattern: cloud mode for everyday Downloads-folder sorts, local mode when you sort a folder of medical records or legal contracts. Sortio reuses the same prompts across modes, so you do not have to rewrite anything when switching.',
  },
];

const LocalVsCloudAiBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'Local AI vs cloud AI for file organization', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Local AI vs cloud AI for file organization',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Local AI vs Cloud AI for File Organization: What Leaves Your Mac?',
    description:
      'An honest comparison of local AI (Ollama, on-device) and cloud AI (OpenAI, Anthropic, Sortio-hosted) for sorting and renaming files. Privacy, speed, accuracy, cost, and how to switch in Sortio.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: PUBLISHED,
    author: {
      '@type': 'Organization',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="Local AI vs Cloud AI for File Organization: What Leaves Your Mac?"
        description="Honest comparison of local AI (Ollama) and cloud AI for file organization. Privacy, speed, accuracy, cost, and how to switch modes in Sortio for sensitive folders."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="local ai file organizer mac, private ai file organizer, offline ai file sorter, ollama file organizer, local llm file sorter, cloud ai file organizer privacy"
        lastModified={PUBLISHED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            Local AI vs Cloud AI for File Organization: What Leaves Your Mac?
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span>By the Sortio team</span>
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>Updated {new Date(PUBLISHED).toLocaleDateString()}</span>
            <span>9 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <p className="text-lg">
            Every AI file organizer on the market makes a privacy decision for you. Most pick
            cloud. Sparkle, the most-discussed AI organizer in the category, runs its AI in the
            cloud and requires an internet connection. The pitch is reasonable (faster,
            higher-accuracy models) and the trade-off is that file content (or at least
            filenames and metadata) is sent to a third-party provider for every sort.
          </p>

          <p>
            For most files this is fine. For sensitive files (medical records, legal contracts,
            tax returns, work files under NDA, anything you would not paste into ChatGPT) it is
            not. The HN threads and r/macapps discussions on this category make the demand clear:
            "only if it works completely local and offline," "entirely local, no sending my
            files anywhere," "I would be somewhat worried about this thing racking up massive
            bills for the larger files." This post is the honest comparison.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <Lock className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  Local AI keeps file content on your Mac and is the right pick for sensitive
                  folders. Cloud AI is faster and more accurate and is the right pick for
                  everyday sorts on non-sensitive content. Sortio supports both via Ollama
                  (local) and Sortio-hosted or BYOK (cloud), and the mode can be toggled per
                  workspace.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            What actually leaves your Mac with each mode
          </h2>

          <p>
            Three modes are worth distinguishing, because they have very different privacy
            properties.
          </p>

          <h3 className="text-xl font-medium text-apple-50 mt-8 mb-3">
            Local mode (Ollama)
          </h3>
          <p>
            The LLM runs on your Mac. When Sortio sorts a folder, it reads file content into a
            local process, runs inference locally, gets back routing decisions and rename
            suggestions, and applies them. No file content, no filenames, no metadata crosses
            the network. The only network traffic is a Sortio license-check ping on app launch
            (a few bytes, no file information attached). For a NAS user worried about a
            10TB-photo migration, or a lawyer sorting client documents, this is the only mode
            that meets the bar.
          </p>

          <h3 className="text-xl font-medium text-apple-50 mt-8 mb-3">
            Sortio-managed cloud
          </h3>
          <p>
            File content is sent to Sortio&apos;s infrastructure, which forwards to the AI
            provider Sortio uses. Sortio does not retain file content beyond the request
            lifetime, the AI provider is on a zero-retention contract for the inference data,
            and the file content is encrypted in transit. This is the default Sortio
            configuration and it is fine for most files: Downloads-folder cleanup, screenshot
            sorting, photo organization. It is not fine for files where the policy requires
            on-device processing.
          </p>

          <h3 className="text-xl font-medium text-apple-50 mt-8 mb-3">
            BYOK (bring your own key)
          </h3>
          <p>
            Sortio routes inference through your own API key with a provider you choose
            (OpenAI, Anthropic, a self-hosted endpoint, an enterprise gateway). The provider
            sees the content, billed to your account. This is the right pick when your
            organization already has an enterprise contract with a specific provider, or when
            you trust one provider but not Sortio&apos;s default. It is still cloud: file
            content is leaving the machine, just to a destination of your choosing.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Speed: how much slower is local?
          </h2>

          <p>
            Benchmarks from a recent run on a 100-file mixed folder (50 invoices, 30
            screenshots, 20 misc PDFs) on an M2 Pro 16GB Mac:
          </p>

          <ul className="list-disc pl-5 space-y-2">
            <li>Sortio-managed cloud: 28 seconds</li>
            <li>BYOK (OpenAI gpt-4-class): 34 seconds</li>
            <li>Local Ollama (Llama 3 8B): 2 minutes 10 seconds</li>
            <li>Local Ollama (Mistral 7B): 1 minute 45 seconds</li>
          </ul>

          <p>
            Local is roughly 4-5x slower in this size range. For a sort run you trigger
            manually and wait on, the difference is the gap between "instant" and "go make
            coffee." For a watch folder that runs in the background, the gap does not matter
            because nothing is waiting on the result.
          </p>

          <p>
            The gap widens for very large sorts because cloud parallelizes harder than a single
            Mac can. A 5,000-file sort might take 8 minutes on cloud and 45 minutes locally.
            That is the threshold where the recommended workflow flips: for large drives, build
            a deterministic rule with{' '}
            <Link
              to="/blog/ai-sort-vs-rule-builder-which-to-use"
              className="text-sortio-primary hover:underline"
            >
              AI Rule Builder
            </Link>
            , which runs locally without consuming AI inference and finishes in seconds even on
            huge folders.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Accuracy: where does local fall short?
          </h2>

          <p>
            Cloud is more accurate on ambiguous classifications. Frontier models (gpt-4-class,
            Claude Sonnet 4.6+, Llama 3 405B) are bigger than anything that fits in consumer
            RAM, and they make fewer mistakes when the routing decision is genuinely hard. For
            file organization the differences show up in three places:
          </p>

          <ul className="list-disc pl-5 space-y-2">
            <li>
              Multi-category documents. A receipt that is also a tax form, an invoice that is
              also a contract amendment. Cloud handles these correctly more often.
            </li>
            <li>
              Unusual vendors and edge formats. A vendor invoice that does not look like a
              typical invoice (a one-line email, a handwritten receipt scan, an Asian-language
              statement) is more likely to be misclassified locally.
            </li>
            <li>
              Filename quality on rename. Both modes produce reasonable filenames, but cloud
              consistently produces shorter, cleaner names. Local sometimes produces names that
              are slightly too verbose or include irrelevant detail.
            </li>
          </ul>

          <p>
            For straightforward folders (Downloads cleanup, screenshot organization, photos by
            EXIF date, standard invoices from a handful of vendors) the accuracy gap is small.
            For complex archives with lots of edge cases, cloud is the right tool unless privacy
            forbids it.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Cost: AI allowance, API spend, and electricity
          </h2>

          <p>
            Local has no per-sort cost. Once Ollama is installed and the model is downloaded,
            inference is free in the dollar sense (electricity is the only cost, which is
            usually trivial). Cloud has per-sort cost: Sortio Free includes a one-time AI trial
            allowance, and Pro at $14.99/month or $99/year includes a monthly AI allowance
            measured over your last 30 days of AI activity, which frees up automatically as
            older usage ages out. BYOK is billed by your provider directly; Sortio does not
            meter your usage at all.
          </p>

          <p>
            For very high-volume users, local is dramatically cheaper. A user sorting 50,000
            files a month would outrun the Pro monthly AI allowance; the same
            user running local Ollama pays only the electricity (under $1/month on an M-series
            Mac for sustained inference). For users sorting a few hundred files a month, the
            cost difference is invisible.
          </p>

          <p>
            The honest framing for cost is: local is best for high-volume, privacy-sensitive,
            or repeatable work where you control the schedule. Cloud is best for one-off sorts
            where speed and accuracy matter more than the per-sort cost.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How to switch modes in Sortio
          </h2>

          <p>
            The switch is in Sortio Settings under AI Mode. Three options: Sortio-managed (the
            default), BYOK (enter your own API key for OpenAI, Anthropic, or a custom
            endpoint), and Ollama Local (point Sortio at your local Ollama install).
          </p>

          <p>
            To set up local mode end-to-end:
          </p>

          <ol className="list-decimal pl-5 space-y-2">
            <li>
              Install Ollama from ollama.com. The installer takes about 30 seconds on a modern
              Mac.
            </li>
            <li>
              Pull a model: ollama pull llama3 (or ollama pull mistral). The download is about
              4.7GB and takes a few minutes on a typical home connection.
            </li>
            <li>
              In Sortio Settings, AI Mode, choose Ollama Local. Sortio will detect the running
              Ollama server (default port 11434) and let you pick which installed model to use.
            </li>
            <li>
              Test on a small folder first. Local Ollama returns slower than you might expect
              on the first sort because of model load time; the second sort is faster because
              the model stays warm.
            </li>
          </ol>

          <p>
            For BYOK, paste your API key into Settings, AI Mode, BYOK. Sortio supports OpenAI
            and Anthropic out of the box, and any OpenAI-compatible endpoint (for enterprise
            gateways or self-hosted vLLM). The key never leaves your Mac; it is stored in the
            macOS Keychain and used only for outbound inference requests.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            When to pick each mode
          </h2>

          <p>
            A practical decision rubric:
          </p>

          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Pick local</strong> when the files are sensitive (legal, medical,
              financial, NDA work), when you process very high volume, or when policy forbids
              third-party AI exposure.
            </li>
            <li>
              <strong>Pick cloud (Sortio-managed)</strong> for everyday sorts on non-sensitive
              content. Downloads-folder cleanup, screenshot organization, photo sorting.
            </li>
            <li>
              <strong>Pick BYOK</strong> when you trust a specific provider (or already have an
              enterprise contract with one) and want the speed and accuracy of frontier models
              with your own billing.
            </li>
          </ul>

          <p>
            The modes are not exclusive. Most heavy Sortio users run two: cloud for daily
            organize-my-Downloads work and local for the specific folders that have sensitive
            content. Sortio stores the AI mode per workspace, so this works cleanly without
            forgetting to switch.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/compare/sparkle-vs-sortio"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Sparkle vs Sortio
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/ai-sort-vs-rule-builder-which-to-use"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                AI Sort vs Rule Builder: which to use when
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/docs/local-models"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Setting up local models in Sortio (docs)
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Try local mode on a sensitive folder</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              Sortio Free includes Ollama local mode. Install Ollama, pull Llama 3 or Mistral,
              and run a sort on a folder where the content cannot leave your Mac. No credit
              card required.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LocalVsCloudAiBlogPage;
